import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Key, Plus, Trash2, Save, X, Tag } from 'lucide-react';
import { useAdminT } from '@/lib/adminTranslations';

const API_BASE = '';

const LANGS = ['en', 'tr', 'es', 'pt', 'de', 'fr', 'it', 'nl', 'ru', 'uk', 'ar', 'id'];
const PAGE_TYPES = ['match', 'league', 'team', 'bookmaker', 'predictions', 'value_bets', 'surebets', 'odds_comparison', 'betting_guide', 'bookmaker_bonus'];

export default function AdminKeywordManager() {
  const t = useAdminT();
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterLang, setFilterLang] = useState('');

  const [form, setForm] = useState({
    page_type: 'match', block_type: 'general', language: 'en',
    primary_keywords: '', secondary_keywords: '', negative_keywords: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('page_type', filterType);
      if (filterLang) params.append('language', filterLang);
      const res = await fetch(`${API_BASE}/api/admin/seo/keywords?${params}`, { headers });
      const data = await res.json();
      setKeywords(data.keywords || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterType, filterLang]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const parseList = (str) => str.split(',').map(s => s.trim()).filter(Boolean);

  const handleCreate = async () => {
    const body = {
      page_type: form.page_type,
      block_type: form.block_type,
      language: form.language,
      primary_keywords: parseList(form.primary_keywords),
      secondary_keywords: parseList(form.secondary_keywords),
      negative_keywords: parseList(form.negative_keywords),
    };
    await fetch(`${API_BASE}/api/admin/seo/keywords`, { method: 'POST', headers, body: JSON.stringify(body) });
    setCreating(false);
    setForm({ page_type: 'match', block_type: 'general', language: 'en', primary_keywords: '', secondary_keywords: '', negative_keywords: '' });
    fetchData();
  };

  const handleUpdate = async () => {
    const body = {
      page_type: form.page_type,
      block_type: form.block_type,
      language: form.language,
      primary_keywords: parseList(form.primary_keywords),
      secondary_keywords: parseList(form.secondary_keywords),
      negative_keywords: parseList(form.negative_keywords),
    };
    await fetch(`${API_BASE}/api/admin/seo/keywords`, { method: 'PUT', headers, body: JSON.stringify(body) });
    setEditIndex(null);
    fetchData();
  };

  const handleDelete = async (kw) => {
    if (!window.confirm('Delete this keyword entry?')) return;
    const params = new URLSearchParams({ page_type: kw.page_type, language: kw.language, block_type: kw.block_type });
    await fetch(`${API_BASE}/api/admin/seo/keywords?${params}`, { method: 'DELETE', headers });
    fetchData();
  };

  const startEdit = (kw, i) => {
    setEditIndex(i);
    setForm({
      page_type: kw.page_type,
      block_type: kw.block_type,
      language: kw.language,
      primary_keywords: (kw.primary_keywords || []).join(', '),
      secondary_keywords: (kw.secondary_keywords || []).join(', '),
      negative_keywords: (kw.negative_keywords || []).join(', '),
    });
  };

  if (loading) return <div className="p-6 text-slate-400">{t.loading}</div>;

  return (
    <div className="space-y-6" data-testid="admin-keyword-manager">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'var(--font-heading)' }}>
          <Key className="w-6 h-6 inline mr-2 text-amber-400" /> {t.seoKeywordManager}
        </h1>
        <Button size="sm" onClick={() => setCreating(true)} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="create-keyword-btn">
          <Plus className="w-4 h-4 mr-1" /> {t.newKeywords}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300" data-testid="kw-filter-type">
          <option value="">All Types</option>
          {PAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300" data-testid="kw-filter-lang">
          <option value="">All Languages</option>
          {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
      </div>

      {/* Create Form */}
      {creating && (
        <div className="bg-slate-900/80 border border-amber-500/30 rounded-xl p-5 space-y-4" data-testid="create-keyword-form">
          <h2 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">New Keyword Entry</h2>
          <div className="grid grid-cols-3 gap-3">
            <select value={form.page_type} onChange={e => setForm({ ...form, page_type: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {PAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input placeholder="Block type (e.g. general, intro)" value={form.block_type}
              onChange={e => setForm({ ...form, block_type: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="input-block-type" />
            <select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200">
              {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <input placeholder="Primary keywords (comma separated)" value={form.primary_keywords}
              onChange={e => setForm({ ...form, primary_keywords: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="input-primary-kw" />
            <input placeholder="Secondary keywords (comma separated)" value={form.secondary_keywords}
              onChange={e => setForm({ ...form, secondary_keywords: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" />
            <input placeholder="Negative keywords (comma separated)" value={form.negative_keywords}
              onChange={e => setForm({ ...form, negative_keywords: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} className="bg-amber-600 hover:bg-amber-500 text-white" data-testid="save-keyword-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)} className="text-slate-400">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Keywords List */}
      <div className="space-y-3">
        {keywords.length === 0 ? (
          <div className="text-center text-slate-500 py-8">No keywords defined. Add SEO keywords to improve AI content quality.</div>
        ) : keywords.map((kw, i) => (
          <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`keyword-item-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="border-slate-700 text-slate-400">{kw.page_type}</Badge>
                <Badge variant="outline" className="border-slate-700 text-slate-400">{kw.block_type}</Badge>
                <Badge variant="outline" className="border-blue-500/30 text-blue-400">{kw.language?.toUpperCase()}</Badge>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => editIndex === i ? setEditIndex(null) : startEdit(kw, i)}
                  className="text-slate-400 hover:text-blue-400 h-7 px-2">
                  <Tag className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(kw)}
                  className="text-slate-400 hover:text-red-400 h-7 px-2" data-testid={`delete-kw-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {editIndex !== i && (
              <div className="flex flex-wrap gap-1 mt-1">
                {(kw.primary_keywords || []).map((k, j) => (
                  <span key={j} className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-2 py-0.5">{k}</span>
                ))}
                {(kw.secondary_keywords || []).map((k, j) => (
                  <span key={`s-${j}`} className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full px-2 py-0.5">{k}</span>
                ))}
                {(kw.long_tail_keywords || []).map((k, j) => (
                  <span key={`lt-${j}`} className="text-xs bg-violet-500/10 text-violet-400 border border-violet-500/20 rounded-full px-2 py-0.5">{k}</span>
                ))}
                {(kw.negative_keywords || []).map((k, j) => (
                  <span key={`n-${j}`} className="text-xs bg-red-500/10 text-red-400 border border-red-500/20 rounded-full px-2 py-0.5 line-through">{k}</span>
                ))}
              </div>
            )}

            {editIndex === i && (
              <div className="space-y-2 mt-3 border-t border-slate-800 pt-3">
                <input placeholder="Primary keywords" value={form.primary_keywords}
                  onChange={e => setForm({ ...form, primary_keywords: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200" />
                <input placeholder="Secondary keywords" value={form.secondary_keywords}
                  onChange={e => setForm({ ...form, secondary_keywords: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200" />
                <input placeholder="Negative keywords" value={form.negative_keywords}
                  onChange={e => setForm({ ...form, negative_keywords: e.target.value })}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleUpdate} className="bg-blue-600 hover:bg-blue-500 text-white">
                    <Save className="w-3 h-3 mr-1" /> Update
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditIndex(null)} className="text-slate-400">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
